import { useState } from "react";
import { toast } from "react-toastify";
// import { useChangePasswordMutation } from "../../../store/api/authApi";
import { useChangePasswordMutation } from "../../store/api/authApi";

import s from "./section.module.css";
import cs from "./ChangePassword.module.css";

const EyeIcon = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {open ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    )}
  </svg>
);

const LockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const strengthLabel = ["", "Weak", "Fair", "Good", "Strong"];
const strengthColor = ["", "#e74c3c", "#f39c12", "#2ecc71", "#016d9c"];

function getStrength(password) {
  let score = 0;
  if (!password) return 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return score;
}

export default function ChangePassword() {
  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const [form, setForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const [show, setShow] = useState({
    currentPassword: false,
    newPassword: false,
    confirmNewPassword: false,
  });

  const strength = getStrength(form.newPassword);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const toggleShow = (field) => {
    setShow((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async () => {
    if (!form.currentPassword || !form.newPassword || !form.confirmNewPassword) {
      toast.error("All fields are required.");
      return;
    }
    if (form.newPassword.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (form.newPassword !== form.confirmNewPassword) {
      toast.error("New passwords do not match.");
      return;
    }
    if (form.currentPassword === form.newPassword) {
      toast.error("New password must be different from current password.");
      return;
    }

    try {
      await changePassword({
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
        confirmNewPassword: form.confirmNewPassword,
      }).unwrap();

      toast.success("Password changed successfully.");
      setForm({ currentPassword: "", newPassword: "", confirmNewPassword: "" });
    } catch (err) {
      const message =
        err?.data?.message ||
        Object.values(err?.data || {}).flat().join(" ") ||
        "Failed to change password.";
      toast.error(message);
    }
  };

  return (
    <div className={cs.wrapper}>
      <div className={cs.intro}>
        <span className={cs.introIcon}><LockIcon /></span>
        <p className={cs.introText}>
          Use a strong password with at least 8 characters, including uppercase letters, numbers, and symbols.
        </p>
      </div>

      {/* Current password */}
      <div className={s.field}>
        <label className={s.label}>Current Password</label>
        <div className={cs.inputWrap}>
          <input
            className={s.input}
            type={show.currentPassword ? "text" : "password"}
            value={form.currentPassword}
            onChange={handleChange("currentPassword")}
            placeholder="Enter current password"
            autoComplete="current-password"
          />
          <button
            type="button"
            className={cs.eyeBtn}
            onClick={() => toggleShow("currentPassword")}
            aria-label="Toggle visibility"
          >
            <EyeIcon open={show.currentPassword} />
          </button>
        </div>
      </div>

      {/* New password */}
      <div className={s.field}>
        <label className={s.label}>New Password</label>
        <div className={cs.inputWrap}>
          <input
            className={s.input}
            type={show.newPassword ? "text" : "password"}
            value={form.newPassword}
            onChange={handleChange("newPassword")}
            placeholder="Enter new password"
            autoComplete="new-password"
          />
          <button
            type="button"
            className={cs.eyeBtn}
            onClick={() => toggleShow("newPassword")}
            aria-label="Toggle visibility"
          >
            <EyeIcon open={show.newPassword} />
          </button>
        </div>

        {/* Strength meter */}
        {form.newPassword && (
          <div className={cs.strengthRow}>
            <div className={cs.strengthBars}>
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className={cs.strengthBar}
                  style={{
                    background: i <= strength ? strengthColor[strength] : "#edf2f6",
                    transition: "background 0.3s",
                  }}
                />
              ))}
            </div>
            <span
              className={cs.strengthLabel}
              style={{ color: strengthColor[strength] }}
            >
              {strengthLabel[strength]}
            </span>
          </div>
        )}
      </div>

      {/* Confirm new password */}
      <div className={s.field}>
        <label className={s.label}>Confirm New Password</label>
        <div className={cs.inputWrap}>
          <input
            className={s.input}
            type={show.confirmNewPassword ? "text" : "password"}
            value={form.confirmNewPassword}
            onChange={handleChange("confirmNewPassword")}
            placeholder="Re-enter new password"
            autoComplete="new-password"
          />
          <button
            type="button"
            className={cs.eyeBtn}
            onClick={() => toggleShow("confirmNewPassword")}
            aria-label="Toggle visibility"
          >
            <EyeIcon open={show.confirmNewPassword} />
          </button>
        </div>

        {/* Match indicator */}
        {form.confirmNewPassword && (
          <span className={cs.matchHint}
            style={{ color: form.newPassword === form.confirmNewPassword ? "#2ecc71" : "#e74c3c" }}>
            {form.newPassword === form.confirmNewPassword ? "✓ Passwords match" : "✗ Passwords do not match"}
          </span>
        )}
      </div>

      <div className={s.btnRow}>
        <button
          className={s.btnPrimary}
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? "Updating…" : "Update Password"}
        </button>
      </div>
    </div>
  );
}